/**
 * Pure helpers + constants for blog article table-of-contents scroll sync.
 * Keeps DOM math and index selection testable and separate from React.
 */

export const CONTENTS_NAV_SCROLL = {
  ACTIVATION_OFFSET_PX: 120,
  END_TOLERANCE_PX: 8,
  NEAR_TOP_SCROLL_PX: 72,
  CLICK_SYNC_SUPPRESS_MS: 480,
} as const;

export function getScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) return null;
  let parent: HTMLElement | null = element.parentElement;

  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const canScroll =
      (overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay") &&
      parent.scrollHeight > parent.clientHeight;
    if (canScroll) return parent;
    parent = parent.parentElement;
  }

  return null;
}

export function getDocumentTop(el: HTMLElement): number {
  const r = el.getBoundingClientRect();
  return r.top + window.scrollY;
}

export function getScrollOffsetInContainer(el: HTMLElement, scrollContainer: HTMLElement): number {
  const cr = scrollContainer.getBoundingClientRect();
  const er = el.getBoundingClientRect();
  return scrollContainer.scrollTop + (er.top - cr.top);
}

export function resolveHeadingElementById(id: string): HTMLElement | null {
  const byId = document.getElementById(id);
  if (byId) return byId;
  if (typeof CSS !== "undefined" && CSS.escape) {
    return document.querySelector<HTMLElement>(`[id="${CSS.escape(id)}"]`);
  }
  return document.querySelector<HTMLElement>(`[id="${id.replace(/"/g, "\\\"")}"]`);
}

/**
 * Which TOC heading index should be active for the current scroll position.
 */
export function computeActiveHeadingIndex(
  headings: HTMLElement[],
  input: {
    scrollTop: number;
    isWindowScroll: boolean;
    scrollContainer: HTMLElement | null;
    viewportBottom: number;
    contentHeight: number;
  },
): number {
  const { ACTIVATION_OFFSET_PX, END_TOLERANCE_PX, NEAR_TOP_SCROLL_PX } = CONTENTS_NAV_SCROLL;

  if (input.viewportBottom >= input.contentHeight - END_TOLERANCE_PX) {
    return headings.length - 1;
  }

  const { scrollTop, isWindowScroll, scrollContainer } = input;
  const activationY = scrollTop + ACTIVATION_OFFSET_PX;

  const offsets = headings.map((h) =>
    isWindowScroll || !scrollContainer
      ? getDocumentTop(h)
      : getScrollOffsetInContainer(h, scrollContainer),
  );

  const candidates = offsets
    .map((top, i) => ({ i, top }))
    .filter(({ top }) => top <= activationY);

  if (candidates.length === 0) return 0;
  if (scrollTop < NEAR_TOP_SCROLL_PX) {
    return candidates.reduce((best, c) => (c.top < best.top ? c : best)).i;
  }
  return candidates.reduce((best, c) => (c.top > best.top ? c : best)).i;
}
